import * as THREE from "three";
import { clearThree, clamp, mobileCheck } from "./helpers.js";
import {
  normalize,
  resolveTarget,
  presets,
  seeded,
  defaults,
} from "./options.js";
import { activate, deactivate } from "./scheduler.js";

export default class VantaBase {
  constructor(input = {}) {
    if (typeof window === "undefined")
      throw new Error("Create effects after the DOM is available");
    if (typeof input === "string" || input?.nodeType === 1)
      input = { el: input };
    this.options = normalize(
      input,
      this.getDefaultOptions?.() || this.defaultOptions || {},
    );
    this.el = resolveTarget(this.options.target || this.options.el);
    this.effect = this.options.effect;
    this.random = seeded(this.options.seed);
    this.status = "pending";
    this.t = 0;
    this.t2 = 0;
    this.scale = 1;
    this.listeners = [];
    this.effectListeners = [];
    this.callbackErrors = [];
    this.styleEdits = [];
    this.visible = true;
    this.userPaused = false;
    this.pointer = { x: 0.5, y: 0.5 };
    this.smooth = { x: 0.5, y: 0.5 };
    this.scroll = 0;
    this.pulse = 0;
    this.deltaFrames = 0;
    this.frameCount = 0;
    this.cost = 0;
    this.adaptiveRatio = mobileCheck() ? 1 : 1.5;
  }
  boot() {
    if (this.booted || this.status === "destroyed") return this;
    this.booted = true;
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.listenInternal(this.motion, "change", () => this.sync());
    this.listenInternal(document, "visibilitychange", () => this.sync());
    this.listenInternal(window, "resize", () => this.scheduleResize());
    this.listenInternal(window, "orientationchange", () =>
      this.scheduleResize(),
    );
    this.listenInternal(this.el, "pointermove", (e) => this.pointerMove(e), {
      passive: true,
    });
    this.listenInternal(
      this.el,
      "pointerdown",
      (e) => {
        this.pointerMove(e);
        if (this.options.clickControls) this.pulse = 1;
      },
      { passive: true },
    );
    this.listenInternal(window, "scroll", () => this.scrollMove(), {
      passive: true,
    });
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.scheduleResize());
      this.ro.observe(this.el);
    }
    if (typeof IntersectionObserver !== "undefined") {
      this.visible = !this.options.lazy;
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      });
      this.io.observe(this.el);
    }
    if (this.options.autoCleanup && typeof MutationObserver !== "undefined") {
      this.mo = new MutationObserver(() => {
        if (!this.el.isConnected) this.destroy();
      });
      this.mo.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
    this.sync();
    return this;
  }
  listenInternal(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push(() =>
      target.removeEventListener(type, handler, options),
    );
  }
  listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.effectListeners.push(() =>
      target.removeEventListener(type, handler, options),
    );
  }
  clearEffectListeners() {
    for (const remove of this.effectListeners) this.captureCallback(remove);
    this.effectListeners = [];
  }
  editStyle(key, value) {
    const style = this.el.style;
    this.styleEdits.push({
      key,
      value,
      old: style.getPropertyValue(key),
      priority: style.getPropertyPriority(key),
      hadStyle: this.el.hasAttribute("style"),
    });
    style.setProperty(key, value);
  }
  restoreStyles() {
    for (const {
      key,
      value,
      old,
      priority,
      hadStyle,
    } of this.styleEdits.reverse())
      if (this.el.style.getPropertyValue(key) === value) {
        if (old) this.el.style.setProperty(key, old, priority);
        else this.el.style.removeProperty(key);
        if (!hadStyle && !this.el.getAttribute("style"))
          this.el.removeAttribute("style");
      }
    this.styleEdits = [];
  }
  prepareEl() {
    const cs = getComputedStyle(this.el);
    if (cs.display === "contents" || cs.display === "inline")
      throw new Error("Use a block or inline-block target with dimensions");
    if (cs.position === "static") this.editStyle("position", "relative");
    if (cs.isolation !== "isolate") this.editStyle("isolation", "isolate");
    this.layer = document.createElement("div");
    this.layer.setAttribute("data-vanta-layer", "");
    this.layer.setAttribute("aria-hidden", "true");
    Object.assign(this.layer.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      borderRadius: "inherit",
      pointerEvents: "none",
      contain: "strict",
    });
    this.el.prepend(this.layer);
    this.applyLayer();
  }
  applyLayer() {
    if (!this.layer) return;
    this.layer.style.zIndex = String(
      this.options.zIndex ?? (this.options.placement === "behind" ? -1 : 0),
    );
    this.layer.style.pointerEvents = this.options.pointerEvents;
  }
  applyCanvasStyles(canvas) {
    canvas.setAttribute("aria-hidden", "true");
    Object.assign(canvas.style, {
      position: "absolute",
      inset: "0",
      display: "block",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
    });
  }
  setSize() {
    this.width = Math.max(1, this.el.clientWidth);
    this.height = Math.max(1, this.el.clientHeight);
    this.scale = mobileCheck() ? this.options.scaleMobile : this.options.scale;
    this.pixelRatio =
      Math.min(
        window.devicePixelRatio || 1,
        this.options.maxPixelRatio,
        this.options.quality === "auto" ? this.adaptiveRatio : 3,
      ) / this.scale;
  }
  start() {
    if (this.status !== "pending") return;
    try {
      this.setSize();
      this.prepareEl();
      if (this.canvas2D) {
        this.canvas = document.createElement("canvas");
        this.layer.append(this.canvas);
        this.applyCanvasStyles(this.canvas);
        this.context = this.canvas.getContext("2d");
        if (!this.context) throw new Error("Canvas2D unavailable");
      } else {
        this.canvas = document.createElement("canvas");
        this.renderer = new THREE.WebGLRenderer({
          canvas: this.canvas,
          alpha: true,
          antialias: this.options.quality === "high",
          powerPreference: "low-power",
        });
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        this.layer.append(this.canvas);
        this.applyCanvasStyles(this.canvas);
        this.scene = new THREE.Scene();
        this.listenInternal(this.canvas, "webglcontextlost", (e) => {
          e.preventDefault();
          this.fail(
            new Error("WebGL context lost; destroy and recreate to retry"),
          );
        });
      }
      this.init();
      this.status = "ready";
      this.resize();
      this.triggerMouseMove(0.5, 0.5);
      this.scrollMove();
      this.renderOnce();
      if (this.options.gyroControls) this.enableGyro(false);
    } catch (error) {
      this.fail(error);
    }
  }
  init() {
    this.onInit?.();
  }
  pointerMove(e) {
    if (
      e.pointerType === "touch"
        ? !this.options.touchControls
        : !this.options.mouseControls
    )
      return;
    const r = this.el.getBoundingClientRect();
    if (!r.width || !r.height) return;
    this.pointer.x = clamp((e.clientX - r.left) / r.width, 0, 1);
    this.pointer.y = clamp((e.clientY - r.top) / r.height, 0, 1);
  }
  scrollMove() {
    if (!this.options.scrollControls) {
      this.scroll = 0;
      return;
    }
    const r = this.el.getBoundingClientRect();
    this.scroll = clamp(
      (innerHeight / 2 - r.top - r.height / 2) / (innerHeight + r.height),
      -0.5,
      0.5,
    );
  }
  async enableGyro(requestPermission = true) {
    if (this.status === "destroyed" || !this.options.gyroControls) return false;
    const api = window.DeviceOrientationEvent;
    if (!api) return false;
    if (typeof api.requestPermission === "function") {
      if (!requestPermission) return false;
      if ((await api.requestPermission()) !== "granted") return false;
    }
    if (this.status === "destroyed") return false;
    if (!this.gyroListening) {
      this.gyroListening = true;
      this.listenInternal(window, "deviceorientation", (e) => {
        if (
          this.options.gyroControls &&
          Number.isFinite(e.gamma) &&
          Number.isFinite(e.beta)
        ) {
          this.pointer.x = clamp(0.5 + e.gamma / 90, 0, 1);
          this.pointer.y = clamp(0.5 + e.beta / 180, 0, 1);
        }
      });
    }
    return true;
  }
  triggerMouseMove(x, y) {
    this.mouseX = x * this.width;
    this.mouseY = y * this.height;
    if (this.uniforms?.iMouse)
      this.uniforms.iMouse.value.set(
        x * this.width * this.pixelRatio,
        y * this.height * this.pixelRatio,
      );
    this.onMouseMove?.(x, y);
  }
  sync() {
    if (["destroyed", "fallback"].includes(this.status)) return;
    const disabled =
      (!this.options.mobile && mobileCheck()) ||
      (this.motion.matches && this.options.reducedMotion === "disable");
    if (this.layer) this.layer.style.display = disabled ? "none" : "";
    if (
      this.status === "pending" &&
      !disabled &&
      (!this.options.lazy || this.visible)
    )
      this.start();
    const animate =
      this.status === "ready" &&
      !disabled &&
      !this.userPaused &&
      !document.hidden &&
      (!this.options.pauseOffscreen || this.visible) &&
      !(this.motion.matches && this.options.reducedMotion !== "ignore");
    this.prevNow = undefined;
    if (animate) activate(this);
    else deactivate(this);
  }
  frame(now) {
    if (this.status !== "ready") return;
    if (this.lastFrame && now - this.lastFrame < 1000 / this.options.fps - 0.5)
      return;
    const dt =
      this.prevNow === undefined
        ? 0
        : Math.min((now - this.prevNow) / 1000, 0.05);
    this.prevNow = now;
    this.lastFrame = now;
    this.deltaFrames = dt * 60 * this.options.speed;
    this.t += this.deltaFrames;
    this.t2 = this.t;
    const f = 1 - Math.pow(1 - this.options.smoothing, Math.max(dt * 60, 1));
    this.smooth.x += (this.pointer.x - this.smooth.x) * f;
    this.smooth.y += (this.pointer.y - this.smooth.y) * f;
    const strength =
      this.options.interactionStrength * this.options.sensitivity;
    this.triggerMouseMove(
      clamp(0.5 + (this.smooth.x - 0.5) * strength, 0, 1),
      clamp(
        0.5 +
          (this.smooth.y - 0.5) * strength +
          this.scroll * this.options.parallax,
        0,
        1,
      ),
    );
    this.pulse *= Math.exp(-dt * 4);
    if ((window.devicePixelRatio || 1) !== this.lastDpr) this.resize();
    const start = performance.now();
    this.renderOnce();
    const cost = performance.now() - start;
    this.cost = this.cost * 0.95 + cost * 0.05;
    if (
      ++this.frameCount % 180 === 0 &&
      this.options.quality === "auto" &&
      this.cost > (1000 / this.options.fps) * 0.65 &&
      this.adaptiveRatio > 0.5
    ) {
      this.adaptiveRatio = Math.max(0.5, this.adaptiveRatio - 0.25);
      this.resize();
    }
  }
  renderOnce() {
    if (this.status !== "ready") return;
    if (this.uniforms?.iTime) this.uniforms.iTime.value = this.t / 60;
    this.onUpdate?.();
    if (this.renderer && this.scene && this.camera) {
      this.renderer.setClearColor(
        this.options.backgroundColor,
        this.options.backgroundAlpha,
      );
      this.renderer.render(this.scene, this.camera);
    }
    this.afterRender?.();
  }
  scheduleResize() {
    if (this.resizeFrame || this.status === "destroyed") return;
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = 0;
      if (this.status === "ready") {
        this.resize();
        if (this.userPaused || this.motion.matches) this.renderOnce();
      }
    });
  }
  resize() {
    this.setSize();
    this.lastDpr = window.devicePixelRatio || 1;
    if (this.camera?.isPerspectiveCamera) {
      this.camera.aspect = this.width / this.height;
      this.camera.updateProjectionMatrix();
    }
    if (this.renderer) {
      this.renderer.setPixelRatio(this.pixelRatio);
      this.renderer.setSize(this.width, this.height, false);
    }
    if (this.context) {
      const w = Math.round(this.width * this.pixelRatio),
        h = Math.round(this.height * this.pixelRatio);
      const changed = this.canvas.width !== w || this.canvas.height !== h;
      if (changed) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
      this.context.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);
      if (changed) this.onResize?.();
    } else this.onResize?.();
    return this;
  }
  setOptions(patch = {}) {
    if (this.status === "destroyed") throw new Error("Instance is destroyed");
    if (
      patch.target !== undefined ||
      patch.el !== undefined ||
      patch.effect !== undefined
    )
      throw new Error("Use create() to change target or effect");
    const merged = { ...this.options, ...patch };
    const preset = patch.quality === "auto" ? defaults : presets[patch.quality];
    if (patch.quality && preset)
      for (const k of ["fps", "maxPixelRatio"])
        if (!(k in patch)) merged[k] = preset[k];
    if (patch.quality === "auto") this.adaptiveRatio = mobileCheck() ? 1 : 1.5;
    const next = normalize(merged);
    const rebuild =
      [
        "quantity",
        "points",
        "spacing",
        "showDots",
        "showLines",
        "size",
        "count",
        "rings",
        "seed",
        "colorMode",
        "birdSize",
        "wingSpan",
      ].some((k) => k in patch && next[k] !== this.options[k]) ||
      (this.effect === "birds" && ("color1" in patch || "color2" in patch));
    this.options = next;
    this.applyLayer();
    if ("autoCleanup" in patch) {
      this.mo?.disconnect();
      if (next.autoCleanup) {
        this.mo = new MutationObserver(() => {
          if (!this.el.isConnected) this.destroy();
        });
        this.mo.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }
    }
    if (rebuild && this.status === "ready") this.restart();
    else if (this.status === "ready") {
      this.updateUniforms?.();
      this.valuesChanger?.();
      this.onOptionsChange?.(patch);
      this.resize();
      this.renderOnce();
    }
    if (patch.gyroControls) this.enableGyro(false);
    this.sync();
    return this;
  }
  update(patch) {
    return this.setOptions(patch);
  }
  pause() {
    this.userPaused = true;
    this.sync();
    return this;
  }
  resume() {
    this.userPaused = false;
    this.sync();
    return this;
  }
  restart() {
    this.clearEffectListeners();
    try {
      this.onDestroy?.();
      clearThree(this.scene);
      this.onRestart?.();
      this.random = seeded(this.options.seed);
      this.init();
      this.resize();
      this.triggerMouseMove(0.5, 0.5);
      this.renderOnce();
    } catch (error) {
      this.fail(error);
    }
    return this;
  }
  captureCallback(task) {
    try {
      task();
    } catch (error) {
      this.callbackErrors.push(error);
    }
  }
  cleanupRendering() {
    if (this.renderingCleaned) return;
    this.renderingCleaned = true;
    this.clearEffectListeners();
    this.captureCallback(() => this.onDestroy?.());
    this.captureCallback(() => clearThree(this.scene));
    this.captureCallback(() => this.renderer?.dispose());
    this.captureCallback(() => this.renderer?.forceContextLoss());
    this.captureCallback(() => this.layer?.remove());
    this.captureCallback(() => this.restoreStyles());
    this.renderer = null;
    this.scene = null;
    this.layer = null;
    this.context = null;
  }
  stopInfrastructure(keepRemovalObserver = false) {
    deactivate(this);
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = 0;
    this.ro?.disconnect();
    this.io?.disconnect();
    if (!keepRemovalObserver) this.mo?.disconnect();
    this.clearEffectListeners();
    for (const remove of this.listeners) remove();
    this.listeners = [];
  }
  fail(error) {
    if (["fallback", "destroyed"].includes(this.status)) return;
    this.status = "fallback";
    this.error = error;
    this.stopInfrastructure(true);
    this.cleanupRendering();
    this.el.dispatchEvent(
      new CustomEvent("vanta:error", { detail: { error, instance: this } }),
    );
    this.captureCallback(() => this.options.onError?.(error, this));
  }
  destroy() {
    if (this.status === "destroyed") return;
    this.status = "destroyed";
    this.stopInfrastructure();
    try {
      this.cleanupRendering();
    } finally {
      this.onDisposed?.();
    }
  }
  getCanvasElement() {
    return this.canvas;
  }
}
